import { all, call, put, select, spawn } from 'redux-saga/effects';

import { assoc, map } from 'ramda';
import { normalize } from 'normalizr';
import * as schema from '../schema';

import * as types from '../constants/ActionTypes';
import { CHAT_GET_ALL_V1 } from '../constants/ApiEndpoints';
import { TIMEOUT_ERROR_REASON } from '../constants/AppConstants';

import { send, startChannelListener } from './index';
import * as actions from '../actions/chatActions';
import { hideShowChatSaga } from './sendSagas';
import { clearErrorSaga } from './helperSagas';

import { httpGet } from '../helpers/HttpUtils';
import { buildSubscriptionEndpoints } from '../helpers/ArrayUtils';
import { isDirectMessage } from '../helpers/ChatUtils';

/**
 * REST
 * Gets all chats and creates channel listeners
 */
export function* getAllChatsSaga(action, numRetries = 0) {
  try {
    const response = yield call(httpGet, CHAT_GET_ALL_V1, { _retries: numRetries });
    const chats = response.data;
    
    yield call(processChats, chats);

    return true;
  } catch (e) {
    yield put(actions.addError(action.type, e));
    return false;
  }
}

/**
 * WS
 * Gets chats based on the ids requested and creates channel listeners
 */
export function* getChatsSaga(action) {
  const response = yield call(send, action);
  if (response) {
    const { chats, successful, reason } = response;
    if (successful) {
      const hasError = yield select(state => state.errors[action.type]);
      if (hasError) {
        yield call(clearErrorSaga, action.type);
      }

      yield call(processChats, chats);

      return true;
    } else {
      yield put(actions.addError(action.type, reason));
      return false;
    }
  } else {
    yield put(actions.addError(action.type, TIMEOUT_ERROR_REASON));
    return false;
  }
}

/**
 * Helper Functions
 */
function* processChats(chats) {
  const { entities: payload } = yield call(normalizeChats, chats);

  // We are using AMQ Topic Exchange for channels only.
  const endpoints = buildSubscriptionEndpoints(chats);
  if (endpoints.length > 0) {
    yield all(map(startChannelListener, endpoints));
  }

  // Update redux state
  yield put({ type: types.GET_CHATS_SUCCESS, payload });

  // Show hidden chats if they received messages while offline
  // This call MUST occur after GET_CHATS_SUCCESS so that the new state does not get overridden
  const hiddenDMsToShow = getDMsThatNeedShown(chats);
  if (hiddenDMsToShow.length > 0) {
    yield spawn(hideShowChatSaga, actions.showChat(hiddenDMsToShow));
  }
}

const normalizeChats = (chats) => {
  // Add default empty messages array to each chat to avoid null error
  const newChats = map(chat => assoc('messages', [], chat), chats);
  return normalize(newChats, schema.chats);
};

// When a user X's out a DM and gets messages on that chat while logged off,
// we want to show the DM because the unread message count reflects those messages and could be confusing otherwise
const getDMsThatNeedShown = (chats) => {
  const directMessages = chats.filter(chat => isDirectMessage(chat));
  return directMessages.filter(chat => chat.hidden && chat.unreadCount > 0).map(chat => chat.id);
};
